import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsDatePickerComponent,
  YdsSpinnerComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  firstDayOfMonthFilter,
  formatDisplayDate,
  isInvalid,
  lastDayOfMonthFilter,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import { GeneralledgerGroupService, type GeneralLedgerGroup } from '../generalledgergroup.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteGeneralLedgerGroupWithChecks } from '../generalledgergroup-shared';

const MAX_END_DATE_ISO = '9999-12-31';

/**
 * Largest "Omschrijving" (GeneralLedger_Group_Name) length the database accepts.
 * The column is nvarchar(50) (EF: GeneralLedgerGroup.Name HasMaxLength(50) in the
 * backend's yfokbjContext.cs); the MaxLength(100) DTO annotation is inaccurate.
 * A value over 50 is rejected on save, so the maximum is 50.
 */
const MAX_NAME_LENGTH = 50;

@Component({
  selector: 'app-generalledgergroup-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsDatePickerComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './generalledgergroup-editable.component.html',
  styleUrl: './generalledgergroup-editable.component.scss',
})
export class GeneralledgergroupEditableComponent {
  readonly isInvalid = isInvalid;
  readonly firstDayOfMonthFilter = firstDayOfMonthFilter;
  readonly lastDayOfMonthFilter = lastDayOfMonthFilter;
  readonly translate = inject(TranslateService);

  private formBuilder = inject(FormBuilder);
  private groupService = inject(GeneralledgerGroupService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Id of the general ledger group being edited, or null in add mode. */
  readonly generalLedgerGroupId = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<GeneralLedgerGroup | null>(null);

  readonly detailsEditing = signal(false);

  readonly form: FormGroup = this.formBuilder.group(
    {
      generalLedgerGroupName: ['', Validators.required],
      startDate: [defaultStartDate(), Validators.required],
      endDate: [null],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator() },
  );

  /** Whether we are creating a new general ledger group (no id in the route). */
  readonly isAddMode = computed(() => this.generalLedgerGroupId() === null);

  /** Display text for the "Omschrijving" value in view mode. */
  readonly groupName = computed(() => this.record()?.generalLedger_Group_Name ?? '');

  readonly startDateDisplay = computed(() => formatDisplayDate(this.record()?.startDate, 'nl-NL'));

  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'n.v.t.' : formatDisplayDate(end, 'nl-NL');
  });

  /** Display text for the "Memo" value in view mode. */
  readonly memoDisplay = computed(() => this.record()?.memo_Value || 'n.v.t.');

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.generalLedgerGroupId.set(id);
      this.loadGroup(id);
    } else {
      // Add mode: start directly in edit state.
      this.detailsEditing.set(true);
    }
  }

  private loadGroup(id: string): void {
    this.isLoading.set(true);
    this.groupService
      .getGeneralLedgerGroupById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (group) => {
          if (!group) {
            this.notFound.set(true);
          } else {
            this.record.set(group);
            this.patchForm(group);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private patchForm(group: GeneralLedgerGroup): void {
    this.form.patchValue({
      generalLedgerGroupName: group.generalLedger_Group_Name,
      startDate: toFormDate(group.startDate),
      endDate: this.isMaxEndDate(group.endDate) ? null : toFormDate(group.endDate),
      memoValue: group.memo_Value ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    const current = this.record();
    if (!current) {
      // No saved record yet (fresh add with nothing to show): go back to the list.
      this.goBack();
      return;
    }

    this.patchForm(current);
    this.detailsEditing.set(false);
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      generalLedgerGroupName: string;
      startDate: Date | null;
      endDate: Date | null;
      memoValue: string;
    };

    if ((value.generalLedgerGroupName?.length ?? 0) > MAX_NAME_LENGTH) {
      this.dialogService.open({
        width: '320px',
        title: 'Tekst te lang',
        message: `De omschrijving mag maximaal ${MAX_NAME_LENGTH} tekens bevatten.`,
        confirmText: 'Ok',
        variant: 'warning',
      });
      return;
    }

    const payload: GeneralLedgerGroup = {
      id: this.generalLedgerGroupId() ?? '',
      generalLedger_Group_Name: value.generalLedgerGroupName,
      startDate: (value.startDate ?? defaultStartDate()) as Date,
      endDate: (value.endDate ?? defaultEndDate()) as Date,
      memo_Value: value.memoValue ?? '',
    };

    this.isSaving.set(true);

    const id = this.generalLedgerGroupId();
    const save$ = id
      ? this.groupService.updateGeneralLedgerGroup(id, payload)
      : this.groupService.addGeneralLedgerGroup(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        const result = { ...payload, ...saved };
        if (!id && result.id) {
          // Reflect the new record's id so the page behaves like edit mode.
          this.generalLedgerGroupId.set(result.id);
        }
        // Stay on the same page in view mode after creating/updating a record.
        this.record.set(result);
        this.patchForm(result);
        this.detailsEditing.set(false);
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }

  // --- Page actions -------------------------------------------------------------

  goBack(): void {
    this.router.navigateByUrl('config/generalledgergroup');
  }

  goToGeneralLedger(): void {
    this.router.navigate(['/config/generalledger']);
  }

  goToGeneralLedgerAccount(): void {
    this.router.navigate(['/config/generalledgeraccount']);
  }

  onDelete(): void {
    const id = this.generalLedgerGroupId();
    if (!id) {
      return;
    }

    deleteGeneralLedgerGroupWithChecks({
      id,
      service: this.groupService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.router.navigateByUrl('config/generalledgergroup'),
    });
  }
}
